// AIDEV-NOTE: Tooltip positioning utilities for tour system
// Calculates optimal tooltip position relative to highlighted elements

import type { TourStepPosition } from "../types/tour-config";

/**
 * AIDEV-NOTE: Resolves a tour step's targetSelector to a valid CSS selector.
 * Handles three cases:
 * 1. Already a data-tour-id attribute selector (starts with "[data-tour-id")
 * 2. A raw CSS selector (e.g., "a[href='...']") that matches elements directly
 * 3. A simple string ID that should be wrapped as [data-tour-id="..."]
 */
export function resolveTourSelector(targetSelector: string): string {
    if (targetSelector.startsWith("[data-tour-id")) {
        return targetSelector;
    }
    // Try the raw selector first — handles CSS selectors like "a[href='...']"
    try {
        if (document.querySelector(targetSelector)) {
            return targetSelector;
        }
    } catch {
        // Invalid CSS selector, fall through to data-tour-id
    }
    return `[data-tour-id="${targetSelector}"]`;
}

export interface ElementRect {
    top: number;
    left: number;
    width: number;
    height: number;
    bottom: number;
    right: number;
}

export interface TooltipPosition {
    top: number;
    left: number;
    position: TourStepPosition;
}

const TOOLTIP_OFFSET = 16; // Gap between tooltip and target element
const VIEWPORT_PADDING = 16; // Minimum distance from viewport edges

/** Get element's bounding rect relative to the viewport */
export function getElementRect(element: HTMLElement): ElementRect {
    const rect = element.getBoundingClientRect();
    return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right,
    };
}

/** Calculate tooltip position based on preferred position and available space */
export function calculateTooltipPosition(
    targetRect: ElementRect,
    tooltipWidth: number,
    tooltipHeight: number,
    preferredPosition: TourStepPosition = "bottom",
): TooltipPosition {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space in each direction
    const spaceTop = targetRect.top - VIEWPORT_PADDING;
    const spaceBottom = viewportHeight - targetRect.bottom - VIEWPORT_PADDING;
    const spaceLeft = targetRect.left - VIEWPORT_PADDING;
    const spaceRight = viewportWidth - targetRect.right - VIEWPORT_PADDING;

    // Check if preferred position has enough space
    const hasSpaceInPreferredPosition = canFitInPosition(
        preferredPosition,
        spaceTop,
        spaceBottom,
        spaceLeft,
        spaceRight,
        tooltipWidth,
        tooltipHeight,
    );

    // Use preferred position if it fits, otherwise find best alternative
    const finalPosition = hasSpaceInPreferredPosition
        ? preferredPosition
        : findBestPosition(
              spaceTop,
              spaceBottom,
              spaceLeft,
              spaceRight,
              tooltipWidth,
              tooltipHeight,
          );

    // Calculate coordinates based on final position
    return calculateCoordinates(
        targetRect,
        tooltipWidth,
        tooltipHeight,
        finalPosition,
    );
}

/** Check if tooltip can fit in a given position */
function canFitInPosition(
    position: TourStepPosition,
    spaceTop: number,
    spaceBottom: number,
    spaceLeft: number,
    spaceRight: number,
    tooltipWidth: number,
    tooltipHeight: number,
): boolean {
    switch (position) {
        case "top":
        case "top-left":
        case "top-right":
            return spaceTop >= tooltipHeight + TOOLTIP_OFFSET;
        case "bottom":
        case "bottom-left":
        case "bottom-right":
            return spaceBottom >= tooltipHeight + TOOLTIP_OFFSET;
        case "left":
            return spaceLeft >= tooltipWidth + TOOLTIP_OFFSET;
        case "right":
            return spaceRight >= tooltipWidth + TOOLTIP_OFFSET;
        case "center":
            return true; // Center always works
        default:
            return false;
    }
}

/** Find the best position based on available space */
function findBestPosition(
    spaceTop: number,
    spaceBottom: number,
    spaceLeft: number,
    spaceRight: number,
    tooltipWidth: number,
    tooltipHeight: number,
): TourStepPosition {
    // Prefer vertical positions (top/bottom) over horizontal
    if (spaceBottom >= tooltipHeight + TOOLTIP_OFFSET) {
        return "bottom";
    }
    if (spaceTop >= tooltipHeight + TOOLTIP_OFFSET) {
        return "top";
    }
    if (spaceRight >= tooltipWidth + TOOLTIP_OFFSET) {
        return "right";
    }
    if (spaceLeft >= tooltipWidth + TOOLTIP_OFFSET) {
        return "left";
    }

    // If nothing fits well, center it
    return "center";
}

/** Calculate exact coordinates for the tooltip */
function calculateCoordinates(
    targetRect: ElementRect,
    tooltipWidth: number,
    tooltipHeight: number,
    position: TourStepPosition,
): TooltipPosition {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (position) {
        case "top":
            top = targetRect.top - tooltipHeight - TOOLTIP_OFFSET;
            left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
            break;

        case "top-left":
            top = targetRect.top - tooltipHeight - TOOLTIP_OFFSET;
            left = targetRect.left;
            break;

        case "top-right":
            top = targetRect.top - tooltipHeight - TOOLTIP_OFFSET;
            left = targetRect.right - tooltipWidth;
            break;

        case "bottom":
            top = targetRect.bottom + TOOLTIP_OFFSET;
            left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
            break;

        case "bottom-left":
            top = targetRect.bottom + TOOLTIP_OFFSET;
            left = targetRect.left;
            break;

        case "bottom-right":
            top = targetRect.bottom + TOOLTIP_OFFSET;
            left = targetRect.right - tooltipWidth;
            break;

        case "left":
            top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
            left = targetRect.left - tooltipWidth - TOOLTIP_OFFSET;
            break;

        case "right":
            top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
            left = targetRect.right + TOOLTIP_OFFSET;
            break;

        case "center":
            top = viewportHeight / 2 - tooltipHeight / 2;
            left = viewportWidth / 2 - tooltipWidth / 2;
            break;
    }

    // Ensure tooltip stays within viewport bounds
    top = Math.max(
        VIEWPORT_PADDING,
        Math.min(top, viewportHeight - tooltipHeight - VIEWPORT_PADDING),
    );
    left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, viewportWidth - tooltipWidth - VIEWPORT_PADDING),
    );

    return { top, left, position };
}
